import React from 'react';
import Popover from '@mui/material/Popover';
import { Button, Box, IconButton, Typography, Dialog, DialogTitle, DialogContent, CircularProgress, Chip } from '@mui/material';
import Axios from 'axios';
import { useSignOut } from "react-auth-kit";
import { useNavigate } from "react-router-dom";
import AttendanceButtonModal from './AttendanceButtonModal';
import Checkbox from '@mui/material/Checkbox';
import SendMessageButtonModal from './SendMessageButtonModal';
import MailIcon from '@mui/icons-material/Mail';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import Tooltip, { tooltipClasses } from '@mui/material/Tooltip';
import StickyNote2OutlinedIcon from '@mui/icons-material/StickyNote2Outlined';
import VideocamIcon from '@mui/icons-material/Videocam';
import MemoButtonModal from './MemoButtonModal';
import ConsultationButtonModal from './ConsultationButtonModal';
import ModeCommentIcon from '@mui/icons-material/ModeComment';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import { styled } from '@mui/material/styles';
// import PublicIcon from '@mui/icons-material/Public';
// import PublicOffIcon from '@mui/icons-material/PublicOff';
import SavingsIcon from '@mui/icons-material/Savings';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import AssessmentIcon from '@mui/icons-material/Assessment';
import GradingIcon from '@mui/icons-material/Grading';
import ForwardToInboxIcon from '@mui/icons-material/ForwardToInbox';
import ParentPortalButton from '../../../components/ParentPortalButton';
import { getAuthHeader } from '../../../utils/authToken';

const StudentButtonMenu = ({
    admin_name,
    userId,
    zoom_id,
    date,
    _time,
    event_idx,
    event,
    attendance,
    enqueueSnackbar,
    colors,
    socketConnected,
    socket,
    setStudentForMoreInfo,
    setBackdropOpen,
    handleBackdropClose,
    setCheckedAttendanceList,
    checkedAttendanceList,
    attendance_len,
    showCheckBox,
    mobileMessageTemplates,
    notesMap,
    setNotesModalOpen,
    setNotesModalData,
    setDataForRequest,
    isThisAdminInViewMode,
    makeupByNewEventId = {},
    autoReportMap,
    awaitingPublishMap = {},
}) => {
    // for 401 error
    const signOut = useSignOut();
    const navigate = useNavigate();
    const handleLogout = () => {
        signOut();
        localStorage.removeItem("userName");
        localStorage.removeItem("who");
        navigate("/signin");
    }
    
    const [anchorEl, setAnchorEl] = React.useState(null);

    // Auto-report modal state
    const [reportModalOpen, setReportModalOpen] = React.useState(false);
    const [reportModalData, setReportModalData] = React.useState(null);
    const [reportModalLoading, setReportModalLoading] = React.useState(false);
    const [emailModalOpen, setEmailModalOpen] = React.useState(false);
    const [emailModalData, setEmailModalData] = React.useState(null);

    const handleOpenReportModal = async (reportUuid, status, completedAt, reportType = 'midterm') => {
        setReportModalOpen(true);
        setReportModalLoading(true);
        try {
            const res = await Axios.get(
                `${process.env.REACT_APP_URL}/api/v1/reports/${reportUuid}`,
                { headers: { Authorization: getAuthHeader() } }
            );
            setReportModalData({ ...res.data, autoReportStatus: status, completedAt, reportType });
        } catch (err) {
            if (err.response?.status === 401) handleLogout();
            else enqueueSnackbar("Failed to load report", { variant: "error" });
            setReportModalOpen(false);
        } finally {
            setReportModalLoading(false);
        }
    };

    const LightTooltip = styled(({ className, ...props }) => (
        <Tooltip {...props} classes={{ popper: className }} />
        )
    )(
        ({ theme }) => ({
            [`& .${tooltipClasses.tooltip}`]: {
                backgroundColor: colors.grey[900],
                color: 'rgba(0, 0, 0, 0.87)',
                boxShadow: theme.shadows[5],
                fontSize: 14,
                border: `1px solid ${colors.grey[700]}`,
            },
        })
    );

    const handleButtonClick = (e) => {
        setAnchorEl(e.currentTarget);

        if (!socketConnected) {
            enqueueSnackbar("Error: Connection to server lost. Please refresh the page.", { variant: "error" });
            return;
        }

        // need to emit to server to let other users know that this user is viewing the course
        socket.emit("put.activeUsers", {
            admin_uuid: userId,
            zoom_id: zoom_id,
            date: date,
            _time: _time,
            event_idx: event_idx,
            active: true,
        });
    };

    const handlePopOverClose = () => {
        setAnchorEl(null);

        if (!socketConnected) {
            enqueueSnackbar("Error: Connection to server lost. Please refresh the page.", { variant: "error" });
            return;
        }

        // need to emit to server to let other users know that this user is no longer viewing the course
        socket.emit("put.activeUsers", {
            admin_uuid: userId,
            zoom_id: zoom_id,
            date: date,
            _time: _time,
            event_idx: event_idx,
            active: false,
        });
    };

    const popoverOpen = Boolean(anchorEl);
    const popoverId = popoverOpen ? 'popover' : undefined;

    async function handleMoreInfoClick() {
        setBackdropOpen(true);
        try {
            const res = await Axios.get(`${process.env.REACT_APP_URL}/api/v1/students/${attendance.student.uuid}`, {
                headers: {
                    Authorization: getAuthHeader(),
                },
            });
            setStudentForMoreInfo(res.data);
        } catch (err) {
            if (err.response?.status === 401) {
                handleLogout();
            } else {
                console.error(err);
                enqueueSnackbar("Error: Cannot get student information at the moment.", { variant: "error" });
            }
        }
        handleBackdropClose();
    }

    function getFontSize(start_end_diff) {
        if (!start_end_diff || start_end_diff === "1.00") {
            return "10px";
        } else {
            return "12px";
        }
    }

    function getStudentFontColor(attendance) {
        if (event.is_canceled_int || attendance.status === 4) {
            return "red !important";
        }
        if (attendance.status === 2 || attendance.status === 5) {
            return"#00B050 !important";
        }
        if (attendance.status !== 3 && attendance.session_number === 1) {
            return colors.blueAccent[600] + " !important";
        }

        return null;
    }

    function getStudentName() {
        let student_name = "";

        if (!attendance.student) {
            student_name += "Name Unknown";
        } else {
            if (attendance.student.first_name)
                student_name += attendance.student.first_name;
            if (attendance.student.korean_name)
                student_name += " " + attendance.student.korean_name;
            if (attendance.student.last_name)
                student_name += " " + attendance.student.last_name;
        }

        student_name += " ";

        if (attendance.student && attendance.student.grade) {
            student_name += "(" + attendance.student.grade + ") ";
        }

        if (attendance.student?.consultation_number) {
            student_name += " (" + attendance.student.consultation_number + ")";
        }

        return student_name;
    }

    function getSessionNumber() {
        let session_number = "";
        if (attendance.session_number !== undefined && attendance.session_number !== null) {
            session_number += "(" + attendance.session_number;
        }

        if (attendance.student_course && attendance.student_course.total_session_num) {
            session_number += "/" + attendance.student_course.total_session_num + ")";
        } else {
            session_number += ")";
        }

        return session_number;
    }

    // only first, korean, last name and consultation number
    function getStudentName2() {
        let student_name = "";

        if (!attendance.student) {
            student_name += "Name Unknown";
        } else {
            if (attendance.student.first_name)
                student_name += attendance.student.first_name;
            if (attendance.student.korean_name)
                student_name += " " + attendance.student.korean_name;
            if (attendance.student.last_name)
                student_name += " " + attendance.student.last_name;
        }

        if (attendance.student?.consultation_number) {
            student_name += " (" + attendance.student.consultation_number + ")";
        }

        return student_name;
    }

    const hasNote = () => {
        if (!notesMap || !attendance || !event) {
            return false;
        }

        if (notesMap[attendance.student_id] && notesMap[attendance.student_id][event.course_id] && notesMap[attendance.student_id][event.course_id][attendance.id]) {
            return true;
        }

        return false;
    }

    const handleClickIsPaid = async (e) => {
        e.stopPropagation();

        // only vice president, manager, and developers can change the is_paid status
        if (admin_name !== "Sungha Hong" && admin_name !== "Amy An" && admin_name !== "Gyuseok Park" && admin_name !== "Keunwoo Song" && admin_name !== "Brian Lee") {
            enqueueSnackbar("Now Allowed.", { variant: "error" });
            return;
        }

        let is_paid = attendance.student_course.is_paid; // current is_paid value
        if (is_paid === 0) {
            is_paid = 1; // change to partially paid
        } else if (is_paid === 1) {
            is_paid = 2; // change to fully paid
        } else if (is_paid === 2) {
            is_paid = 0; // change to not paid
        } else {
            is_paid = 0; // change to not paid
        }
        

        try {
            // emit to server
            socket.emit("put.IsPaidUpdate", {
                student_course_id: attendance.student_course.id,
                is_paid: is_paid,
            });
        } catch (error) {
            console.error(error);
            enqueueSnackbar("Error: Cannot save the changes. Please try again.", { variant: "error" });
            return;
        }
    };

    // const addressCountryInfoOmitted = (address) => {
    //     if (!address || !address.country) {
    //         return true;
    //     }

    //     const country = address.country.toLowerCase().trim();

    //     if (country === "") {
    //         return true;
    //     }

    //     return false;
    // };

    // const doesStudentLiveOutsideUS = (address) => {
    //     const country = address.country.toLowerCase().trim();
    //     return country !== "united states" && country !== "us" && country !== "usa";
    // };

    return (<>
        <Box
            sx={{
                position: "absolute",
                right: "0",
                top: "0",
                display: 'flex',
                flexDirection: 'row',
                gap: '5px',
                zIndex: 1,
            }}
        >
            {/* Awaiting-publish indicator: M / F badges if this (student, course) has
                a midterm/final session that's submitted/graded but not yet published.
                Hidden when nothing pending — matches existing icon convention.
                Hover shows test list with days-since-submit; click opens TestResults
                pre-filtered to this student + course. No 14-day cap (operational view). */}
            {(() => {
                const apKey = `${attendance.student_id}_${event.course_id}`;
                const apEntries = awaitingPublishMap[apKey];
                if (!apEntries || apEntries.length === 0) return null;

                const midtermEntry = apEntries.find(e => e.category === 'midterm') || null;
                const finalEntry = apEntries.find(e => e.category === 'final') || null;

                const statusLabel = {
                    not_started: 'Not started',
                    in_progress: 'In progress',
                    grading: 'FRQ grading…',
                    graded: 'Graded — needs publish',
                    published: 'Published',
                };

                // Per-category color: bright green when published (done), grey otherwise.
                const colorFor = (entry) => entry?.status === 'published' ? '#66bb6a' : '#757575';

                const handleApClick = (category) => (e) => {
                    e.stopPropagation();
                    const first = attendance.student?.first_name || '';
                    const last = attendance.student?.last_name || '';
                    const searchName = [first, last].filter(Boolean).join(' ').trim();
                    const params = new URLSearchParams();
                    params.set('courseIds', String(event.course_id));
                    if (searchName) params.set('searchQuery', searchName);
                    if (category) params.set('testCategory', category);
                    params.set('clearOthers', '1');
                    window.open(`/admin/testResults?${params.toString()}`, '_blank');
                };

                const baseBadgeSx = {
                    color: 'white',
                    fontSize: '11px',
                    fontWeight: 700,
                    px: '5px',
                    py: '1px',
                    borderRadius: '3px',
                    lineHeight: 1.4,
                    minWidth: '14px',
                    textAlign: 'center',
                    cursor: 'pointer',
                };

                // Single-entry tooltip for one badge — shows that category's status only.
                const renderBadge = (entry, label) => {
                    if (!entry) return null;
                    const tooltip = (
                        <Box>
                            <Typography variant="caption" sx={{ fontWeight: 600, display: 'block' }}>
                                {label === 'M' ? 'Midterm' : 'Final'}
                            </Typography>
                            <Typography variant="caption" sx={{ display: 'block' }}>
                                {statusLabel[entry.status] || entry.status}
                            </Typography>
                            <Typography variant="caption" sx={{ display: 'block', mt: 0.5, fontStyle: 'italic', opacity: 0.7 }}>
                                Click to open Test Results
                            </Typography>
                        </Box>
                    );
                    return (
                        <Tooltip title={tooltip} arrow placement="bottom">
                            <Box onClick={handleApClick(entry.category)} sx={{ ...baseBadgeSx, bgcolor: colorFor(entry) }}>{label}</Box>
                        </Tooltip>
                    );
                };

                return (
                    <>
                        {renderBadge(midtermEntry, 'M')}
                        {renderBadge(finalEntry, 'F')}
                    </>
                );
            })()}
            {/* {attendance.student && addressCountryInfoOmitted(attendance.student.address) ? (
                <Tooltip title="Country info not specified" arrow placement='bottom'>
                    <PublicOffIcon fontSize='medium' color='warning' />
                </Tooltip>
            ) : attendance.student && doesStudentLiveOutsideUS(attendance.student.address) ? (
                <Tooltip title="Student lives outside US" arrow placement='bottom'>
                    <PublicIcon fontSize='medium' color='info' />
                </Tooltip>
            ) : null} */}
            {attendance.student_course && attendance.student_course.is_paid === 0 ? (
                <IconButton sx={{ padding: 0 }} onClick={(e) => handleClickIsPaid(e)} disabled={isThisAdminInViewMode}>
                    <SavingsIcon fontSize='medium' color='error' />
                </IconButton>
            ) : attendance.student_course && attendance.student_course.is_paid === 1 ? (
                <IconButton sx={{ padding: 0 }} onClick={(e) => handleClickIsPaid(e)} disabled={isThisAdminInViewMode}>
                    <SavingsIcon fontSize='medium' color='success' />
                </IconButton>
            ) : attendance.student_course && attendance.student_course.is_paid === 2 ? (
                <IconButton sx={{ padding: 0 }} onClick={(e) => handleClickIsPaid(e)} disabled={isThisAdminInViewMode}>
                    <SavingsIcon fontSize='medium' color='info' />
                </IconButton>
            ) : null}
            {attendance.status === 3 && attendance.sent_cancel_reminder === false ? (
                <Tooltip title="Need to send cancel reminder" arrow placement='bottom'>
                    <MailIcon fontSize='medium' color='error' />
                </Tooltip>
            ) : attendance.status === 3 && attendance.sent_cancel_reminder === true ? (
                <Tooltip title="✔ Cancel reminder sent" arrow placement='bottom'>
                    <MarkEmailReadIcon fontSize='medium' color='error' />
                </Tooltip>
            ) : null}
            {attendance.status !== 3 && attendance.session_number === 1 && attendance.sent_first_class_reminder === false ? (
                <Tooltip title="Need to send first class reminder" arrow placement='bottom'>
                    <MailIcon fontSize='medium' sx={{ color: colors.blueAccent[600] }}  />
                </Tooltip>
            ) : attendance.status !== 3 && attendance.session_number === 1 && attendance.sent_first_class_reminder === true ? (
                <Tooltip title="✔ First class reminder sent" arrow placement='bottom'>
                    <MarkEmailReadIcon fontSize='medium' sx={{ color: colors.blueAccent[600] }} />
                </Tooltip>
            ) : null}
            {event.is_make_up_event && attendance.status !== 3 && attendance.sent_makeup_reminder === false ? (
                <Tooltip title="Need to send makeup class reminder" arrow placement='bottom'>
                    <MailIcon fontSize='medium' sx={{ color: colors.greenAccent[400] }} />
                </Tooltip>
            ) : event.is_make_up_event && attendance.status !== 3 && attendance.sent_makeup_reminder === true ? (
                <Tooltip title="✔ Makeup class reminder sent" arrow placement='bottom'>
                    <MarkEmailReadIcon fontSize='medium' sx={{ color: colors.greenAccent[400] }} />
                </Tooltip>
            ) : null}
            {event.is_make_up_event && makeupByNewEventId[event.id] && attendance.student_course?.start_date &&
             new Date(attendance.student_course.start_date) > new Date(makeupByNewEventId[event.id].og_event_date) ? (
                <Tooltip title="Late joiner - enrolled after original class was canceled. Please contact manually." arrow placement='bottom'>
                    <WarningAmberIcon fontSize='medium' sx={{ color: colors.redAccent[500] }} />
                </Tooltip>
            ) : null}
            {hasNote() && (
                <IconButton
                    color='primary'
                    onClick={(e) => {
                        e.stopPropagation();
                        setNotesModalData([notesMap[attendance.student_id][event.course_id][attendance.id]]);
                        setNotesModalOpen(true);
                        setDataForRequest({
                            course_id: event.course_id,
                            student_name: getStudentName2(),
                        });
                    }}
                    sx={{
                        padding: 0,
                    }}
                >
                    <Tooltip title="Memo" placement="top" arrow>
                        <StickyNote2OutlinedIcon fontSize='medium' />
                    </Tooltip>
                </IconButton>
            )}
            {attendance.need_zoom_recording && !attendance.zoom_recording_uploaded ? (
                <Tooltip title="Need to upload zoom recording on classroom" arrow placement='top'>
                    <VideocamIcon fontSize='medium' color='success' />
                </Tooltip>
            ) : null}
            {attendance.student_course && attendance.student_course.memo && attendance.student_course.memo !== "" && (
                <LightTooltip
                    title={
                        attendance.student_course.memo.split("\n").map((item, key) => {
                            return <span key={key}>{item}<br/></span>
                        })
                    }
                    placement="right-start"
                    arrow
                >
                    <ModeCommentIcon fontSize='small' color='info' />
                </LightTooltip>
            )}
            {autoReportMap && attendance.student_course && autoReportMap[attendance.student_course.id]?.midterm_report && (() => {
                const info = autoReportMap[attendance.student_course.id].midterm_report;
                const sentDate = info.completed_at ? new Date(info.completed_at).toLocaleDateString() : '';
                const statusConfig = {
                    email_sent: { color: '#4CAF50', tooltip: `Midterm report sent to parent${sentDate ? ` on ${sentDate}` : ''} (click to view)` },
                    email_failed: { color: '#f44336', tooltip: 'Midterm report generated but email delivery failed — will auto-retry daily (click to view report)' },
                    failed: { color: '#f44336', tooltip: 'Midterm report generation failed — will retry if student is still at midpoint' },
                    processing: { color: '#FF9800', tooltip: 'Midterm report is being generated...' },
                    pending: { color: '#FF9800', tooltip: 'Midterm report is queued for generation' },
                    preview: { color: '#2196F3', tooltip: 'Midterm report preview — awaiting approval before sending' },
                    preview_ready: { color: '#2196F3', tooltip: 'Midterm report preview ready — awaiting approval before sending' },
                };
                const cfg = statusConfig[info.status] || { color: '#FF9800', tooltip: `Midterm report: ${info.status}` };
                return (
                    <Tooltip title={cfg.tooltip} arrow placement='bottom'>
                        <AssessmentIcon
                            fontSize='small'
                            sx={{
                                color: cfg.color,
                                cursor: info.report_uuid ? 'pointer' : 'default',
                            }}
                            onClick={(e) => {
                                e.stopPropagation();
                                if (info.report_uuid) {
                                    handleOpenReportModal(info.report_uuid, info.status, info.completed_at);
                                }
                            }}
                        />
                    </Tooltip>
                );
            })()}
            {autoReportMap && attendance.student_course && autoReportMap[attendance.student_course.id]?.three_remaining_email && (() => {
                const info = autoReportMap[attendance.student_course.id].three_remaining_email;
                const hasContent = info.email_subject || info.email_content;
                const sentDate = info.completed_at ? new Date(info.completed_at).toLocaleDateString() : '';
                const statusConfig = {
                    email_sent: { color: '#4CAF50', tooltip: `3-remaining email sent to parent${sentDate ? ` on ${sentDate}` : ''} (click to view)` },
                    email_failed: { color: '#f44336', tooltip: '3-remaining email delivery failed — will auto-retry daily (click to view content)' },
                    failed: { color: '#f44336', tooltip: '3-remaining email generation failed — will retry if student still has 3 classes left' },
                    processing: { color: '#FF9800', tooltip: '3-remaining email is being generated...' },
                    pending: { color: '#FF9800', tooltip: '3-remaining email is queued for generation' },
                    preview: { color: '#2196F3', tooltip: '3-remaining email preview — awaiting approval before sending' },
                    preview_ready: { color: '#2196F3', tooltip: '3-remaining email preview ready — awaiting approval before sending' },
                };
                const cfg = statusConfig[info.status] || { color: '#FF9800', tooltip: `3-remaining email: ${info.status}` };
                return (
                    <Tooltip title={cfg.tooltip} arrow placement='bottom'>
                        <ForwardToInboxIcon
                            fontSize='small'
                            sx={{
                                color: cfg.color,
                                cursor: hasContent ? 'pointer' : 'default',
                            }}
                            onClick={(e) => {
                                e.stopPropagation();
                                if (hasContent) {
                                    setEmailModalData({ subject: info.email_subject, content: info.email_content, recipient_email: info.recipient_email, completed_at: info.completed_at, status: info.status });
                                    setEmailModalOpen(true);
                                }
                            }}
                        />
                    </Tooltip>
                );
            })()}
            {autoReportMap && attendance.student_course && autoReportMap[attendance.student_course.id]?.final_report && (() => {
                const info = autoReportMap[attendance.student_course.id].final_report;
                const publishedDate = info.completed_at ? new Date(info.completed_at).toLocaleDateString() : '';
                return (
                    <Tooltip title={`Final report published${publishedDate ? ` on ${publishedDate}` : ''} (click to view)`} arrow placement='bottom'>
                        <GradingIcon
                            fontSize='small'
                            sx={{
                                color: '#4CAF50',
                                cursor: info.report_uuid ? 'pointer' : 'default',
                            }}
                            onClick={(e) => {
                                e.stopPropagation();
                                if (info.report_uuid) {
                                    handleOpenReportModal(info.report_uuid, info.status, info.completed_at, 'final');
                                }
                            }}
                        />
                    </Tooltip>
                );
            })()}
        </Box>
        {attendance.student_course && attendance.student_course.student_course_consultations &&
            attendance.student_course.student_course_consultations.length > 0 && (
            <Box
                sx={{
                    position: "absolute",
                    left: 0,
                    top: 0,
                    display: 'flex',
                    flexDirection: 'row',
                    // cap the row so many consultations wrap below instead of
                    // running into the right-side icon row
                    flexWrap: 'wrap',
                    maxWidth: '40%',
                    gap: '2px',
                    zIndex: 1,
                    // keep clear of the selection checkbox, which also sits at the top-left
                    ml: showCheckBox ? `${Math.max(32 / attendance_len, 20) + 8}px` : 0,
                }}
            >
                {attendance.student_course.student_course_consultations.map((consultation) => (
                    <LightTooltip
                        key={"consultation-" + consultation.id}
                        title={
                            (consultation.memo || "").split("\n").map((item, key) => {
                                return <span key={key}>{item}<br/></span>
                            })
                        }
                        placement="top"
                        arrow
                    >
                        <SupportAgentIcon fontSize='small' sx={{ color: colors.purpleAccent[500] }} />
                    </LightTooltip>
                ))}
            </Box>
        )}
        {showCheckBox &&
            <Box
                sx={{
                    position: "absolute",
                    left: 0,
                    top: 0,
                    zIndex: 1,
                }}
            >
                <Checkbox
                    onChange={(e) => {
                        e.stopPropagation();
                        if (e.target.checked) {
                            setCheckedAttendanceList((prev) => {
                                const _attendance = { ...attendance, course_id: event.course_id };
                                return [...prev, _attendance];
                            });
                        } else {
                            const newCheckedAttendanceList = checkedAttendanceList.filter((item) => item.id !== attendance.id);
                            setCheckedAttendanceList(newCheckedAttendanceList);
                        }
                    }}
                    sx={{ 
                        '& .MuiSvgIcon-root': { 
                            fontSize: Math.max(32 / attendance_len, 20),
                        },
                    }}
                />
            </Box>
        }
        <Button
            sx={{
                fontSize: getFontSize(event.start_end_diff),
                textAlign: "center",
                width: "100%",
                height: "100%",
                padding: "0px",
                color: getStudentFontColor(attendance),
                textTransform: 'none',
            }}
            disabled={isThisAdminInViewMode}
            onClick={(e) => handleButtonClick(e)}
        >
            {getStudentName()} <Typography sx={{ ml: 0.5, color: colors.greenAccent[400] }}>{getSessionNumber()}</Typography>
        </Button>
        <Popover
            id={popoverId}
            open={popoverOpen}
            anchorEl={anchorEl}
            onClose={handlePopOverClose}
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'center',
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    bgcolor: "background.paper",
                    width: "200px",
                    boxShadow: 4,
                }}
            >
                <Button 
                    color='primary'
                    sx={{ 
                        p: 1, 
                        width: "100%",
                        bgcolor: colors.primary[800],
                        borderRadius: "0px",
                    }}
                    onClick={handleMoreInfoClick}
                    disabled={isThisAdminInViewMode}
                >
                    More Info
                </Button>
                <AttendanceButtonModal
                    admin_name={admin_name}
                    userId={userId}
                    zoom_id={zoom_id}
                    date={date}
                    _time={_time}
                    event_idx={event_idx}
                    event={event}
                    attendance={attendance}
                    enqueueSnackbar={enqueueSnackbar}
                    colors={colors}
                    handlePopOverClose={handlePopOverClose}
                    socket={socket}
                    socketConnected={socketConnected}
                    mobileMessageTemplates={mobileMessageTemplates}
                />
                <SendMessageButtonModal
                    admin_name={admin_name}
                    userId={userId}
                    zoom_id={zoom_id}
                    date={date}
                    _time={_time}
                    event_idx={event_idx}
                    event={event}
                    attendance={attendance}
                    enqueueSnackbar={enqueueSnackbar}
                    colors={colors}
                    handlePopOverClose={handlePopOverClose}
                    socket={socket}
                    socketConnected={socketConnected}
                    mobileMessageTemplates={mobileMessageTemplates}
                />
                <MemoButtonModal
                    admin_name={admin_name}
                    userId={userId}
                    zoom_id={zoom_id}
                    date={date}
                    _time={_time}
                    event_idx={event_idx}
                    event={event}
                    attendance={attendance}
                    enqueueSnackbar={enqueueSnackbar}
                    colors={colors}
                    handlePopOverClose={handlePopOverClose}
                    socket={socket}
                    socketConnected={socketConnected}
                />
                <ConsultationButtonModal
                    admin_name={admin_name}
                    event={event}
                    attendance={attendance}
                    enqueueSnackbar={enqueueSnackbar}
                    colors={colors}
                    handlePopOverClose={handlePopOverClose}
                    socket={socket}
                    socketConnected={socketConnected}
                />
                <ParentPortalButton
                    parentEmail={attendance.student?.parent_email}
                    consultationNumber={attendance.student?.consultation_number}
                    studentUuid={attendance.student?.uuid}
                    courseId={event.course_id}
                    variant="dropdown"
                    disabled={isThisAdminInViewMode}
                    sx={{ bgcolor: colors.primary[800] }}
                    onSuccess={handlePopOverClose}
                />
            </Box>
        </Popover>
        <Dialog
            open={reportModalOpen}
            onClose={() => { setReportModalOpen(false); setReportModalData(null); }}
            maxWidth="md"
            fullWidth
        >
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    {reportModalData?.reportType === 'final' ? 'Final Report' : 'Midterm Report'}
                    {reportModalData?.autoReportStatus && (
                        <Chip
                            label={reportModalData.autoReportStatus === 'email_sent' ? 'Sent' : reportModalData.autoReportStatus.replace(/_/g, ' ')}
                            size="small"
                            sx={{
                                fontWeight: 700,
                                fontSize: '0.7rem',
                                height: 22,
                                bgcolor: reportModalData.autoReportStatus === 'email_sent' ? '#1e7e34' : reportModalData.autoReportStatus === 'email_failed' ? '#c5221f' : '#e37400',
                                color: '#fff',
                            }}
                        />
                    )}
                </Box>
                {reportModalData && (
                    <Box sx={{ textAlign: 'right' }}>
                        <Typography variant="body2" color="textSecondary">
                            {reportModalData.student?.first_name} {reportModalData.student?.last_name} — {reportModalData.course?.course_title}
                        </Typography>
                        {reportModalData.completedAt && (
                            <Typography variant="caption" color="textSecondary">
                                {reportModalData.reportType === 'final' ? 'Published on' : 'Sent on'} {new Date(reportModalData.completedAt).toLocaleString()}
                            </Typography>
                        )}
                    </Box>
                )}
            </DialogTitle>
            <DialogContent>
                {reportModalLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : reportModalData?.report_html ? (
                    <iframe
                        srcDoc={reportModalData.report_html}
                        style={{ width: '100%', height: '70vh', border: 'none', backgroundColor: '#fff' }}
                        title={reportModalData?.reportType === 'final' ? 'Final Report Preview' : 'Midterm Report Preview'}
                    />
                ) : (
                    <Typography color="textSecondary" sx={{ py: 2 }}>No report content available.</Typography>
                )}
            </DialogContent>
        </Dialog>
        <Dialog
            open={emailModalOpen}
            onClose={() => { setEmailModalOpen(false); setEmailModalData(null); }}
            maxWidth="md"
            fullWidth
            PaperProps={{ sx: { borderRadius: '12px', overflow: 'hidden' } }}
        >
            {emailModalData ? (
                <Box>
                    {/* Email header bar */}
                    <Box sx={{
                        bgcolor: '#f2f6fc',
                        borderBottom: '1px solid #e0e0e0',
                        px: 3, py: 2,
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                {emailModalData.subject || '(no subject)'}
                            </Typography>
                            <Chip
                                label={emailModalData.status === 'email_sent' ? 'Sent' : emailModalData.status?.replace(/_/g, ' ')}
                                size="small"
                                sx={{
                                    fontWeight: 700,
                                    fontSize: '0.7rem',
                                    height: 22,
                                    bgcolor: emailModalData.status === 'email_sent' ? '#1e7e34' : emailModalData.status === 'email_failed' ? '#c5221f' : '#e37400',
                                    color: '#fff',
                                }}
                            />
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="caption" sx={{ color: '#5f6368', minWidth: 32 }}>From</Typography>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>A ONE Institute &lt;aone@a-oneinstitute.com&gt;</Typography>
                            </Box>
                            {emailModalData.recipient_email && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography variant="caption" sx={{ color: '#5f6368', minWidth: 32 }}>To</Typography>
                                    <Typography variant="body2">{emailModalData.recipient_email}</Typography>
                                </Box>
                            )}
                            {emailModalData.completed_at && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography variant="caption" sx={{ color: '#5f6368', minWidth: 32 }}>Date</Typography>
                                    <Typography variant="body2">{new Date(emailModalData.completed_at).toLocaleString()}</Typography>
                                </Box>
                            )}
                        </Box>
                    </Box>
                    {/* Email body */}
                    <Box sx={{ px: 3, py: 3, maxHeight: '60vh', overflowY: 'auto' }}>
                        <Box sx={{
                            whiteSpace: 'pre-wrap',
                            fontSize: '0.95rem',
                            lineHeight: 1.8,
                            color: '#202124',
                            fontFamily: '"Noto Sans KR", "Roboto", sans-serif',
                        }}>
                            {emailModalData.content || '(no content)'}
                        </Box>
                    </Box>
                </Box>
            ) : (
                <DialogContent>
                    <Typography color="textSecondary" sx={{ py: 2 }}>No email content available.</Typography>
                </DialogContent>
            )}
        </Dialog>
        </>
    )
}
export default StudentButtonMenu;